import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useLike } from '../hooks/useLike'
import OnlineDot from './OnlineDot'
import { decodeEntities } from '../lib/decodeEntities'

const COVER_GRADIENTS = [
  'linear-gradient(135deg, #2D1040, #5C1A3A)',
  'linear-gradient(135deg, #1a2e1f, #0F6E56)',
  'linear-gradient(135deg, #2E1A0E, #854F0B)',
  'linear-gradient(135deg, #1a1040, #534AB7)',
  'linear-gradient(135deg, #3B1A1A, #9B2C2C)',
  'linear-gradient(135deg, #1A2840, #2563EB)',
]

const AVATAR_COLORS = [
  'linear-gradient(135deg, #7C3AED, #EC4899)',
  'linear-gradient(135deg, #0D9488, #1D4ED8)',
  'linear-gradient(135deg, #EA580C, #EC4899)',
  'linear-gradient(135deg, #059669, #0D9488)',
  'linear-gradient(135deg, #BE123C, #7C3AED)',
  'linear-gradient(135deg, #1D4ED8, #14B8A6)',
]

function formatNum(n) {
  if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k'
  return String(n)
}

export default function QuizCard({ quiz, index = 0, saved = false, onSaveToggle = null, currentUserId = null, onDelete = null }) {
  const navigate = useNavigate()
  const coverGradient = COVER_GRADIENTS[quiz.coverIdx ?? index % COVER_GRADIENTS.length]
  const avatar = AVATAR_COLORS[index % AVATAR_COLORS.length]
  const isPersonality = quiz.type === 'personality'
  const { liked, count: likeCount, toggle: toggleLike } = useLike('quiz', quiz.id, quiz.likes, quiz.likedByMe)
  const isOwner = currentUserId && quiz.userId === currentUserId

  const [menuOpen, setMenuOpen]       = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting, setDeleting]       = useState(false)

  async function handleDelete(e) {
    e.preventDefault()
    e.stopPropagation()
    setDeleting(true)
    try {
      await onDelete(quiz.id)
    } finally {
      setDeleting(false)
      setMenuOpen(false)
      setConfirmDelete(false)
    }
  }

  return (
    <div className="quiz-card-wrap">
      <Link to={quiz.published === false ? `/quiz/edit/${quiz.id}` : `/quiz/${quiz.id}`} className="quiz-card">
        <div
          className="quiz-card__cover"
          style={quiz.coverUrl
            ? { backgroundImage: `url(${quiz.coverUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }
            : { background: coverGradient }
          }
        >
          <span className={`quiz-card__type-badge quiz-card__type-badge--${quiz.type}`}>
            {isPersonality ? '🌀 Personality' : '⚡ Knowledge'}
          </span>

          {onSaveToggle && (
            <button
              className={`quiz-card__save-btn${saved ? ' quiz-card__save-btn--saved' : ''}`}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onSaveToggle(quiz.id, saved) }}
              title={saved ? 'Remove from saved' : 'Save quiz'}
              aria-label={saved ? 'Remove from saved' : 'Save quiz'}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill={saved ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
              </svg>
            </button>
          )}
        </div>

        <div className="quiz-card__body">
          <div className="quiz-card__author-row">
            <div className="quiz-card__avatar" style={{ position: 'relative', background: quiz.authorAvatarUrl ? undefined : (quiz.authorAvatarGrad || avatar) }}>
              {quiz.authorAvatarUrl
                ? <img src={quiz.authorAvatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                : (quiz.author?.[0]?.toUpperCase() ?? '?')}
              <OnlineDot userId={quiz.userId} size={8} fallback={quiz.authorIsOnline} />
            </div>
            <span className="quiz-card__author">{decodeEntities(quiz.author)}</span>
            {quiz.fandom && <span className="quiz-card__fandom">{decodeEntities(quiz.fandom)}</span>}
          </div>
          <h3 className="quiz-card__title">{decodeEntities(quiz.title)}</h3>
          {quiz.description && <p className="quiz-card__desc">{decodeEntities(quiz.description)}</p>}
          <div className="quiz-card__footer">
            <span className="quiz-card__stat">▶ {formatNum(quiz.plays ?? 0)} plays</span>
            <button
              className={`quiz-card__stat quiz-card__like-btn${liked ? ' quiz-card__like-btn--liked' : ''}`}
              onClick={toggleLike}
              aria-label={liked ? 'Unlike' : 'Like'}
            >
              {liked ? '❤️' : '🤍'} {formatNum(likeCount)}
            </button>
          </div>
        </div>
      </Link>

      {/* Owner ••• menu */}
      {isOwner && (
        <div className="quiz-card__menu-wrap">
          <button
            className="quiz-card__menu-btn"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMenuOpen(v => !v); setConfirmDelete(false) }}
            aria-label="Quiz options"
          >
            •••
          </button>
          {menuOpen && (
            <div className="quiz-card__menu">
              {!confirmDelete ? (
                <>
                  <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMenuOpen(false); navigate(`/quiz/edit/${quiz.id}`) }}>
                    Edit
                  </button>
                  <button className="danger" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setConfirmDelete(true) }}>
                    Delete
                  </button>
                </>
              ) : (
                <div className="quiz-card__menu-confirm" onClick={e => { e.preventDefault(); e.stopPropagation() }}>
                  <p>Delete this quiz?</p>
                  <div className="quiz-card__menu-confirm-actions">
                    <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); setConfirmDelete(false) }}>Cancel</button>
                    <button className="danger" onClick={handleDelete} disabled={deleting}>
                      {deleting ? '…' : 'Yes, delete'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
